/**
 * Play — Main game play page.
 *
 * Flow per stretch:
 *   1. Choose Activity  → pick what to do (no movement yet)
 *   2. Move & Commit    → movement budget based on activity, then Lock In
 *   3. Narrative         → AI GM narrates, player interacts (ASK/SAY/DO/OMEN)
 *   4. Next Stretch      → back to step 1
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { GridMapCanvas } from '@/components/game-ui/GridMapCanvas';
import { PlaySidebar } from '@/components/game-ui/PlaySidebar';
import { NarrativePanel } from '@/components/game-ui/NarrativePanel';
import { DialogueOverlay, type DialogueState } from '@/components/game-ui/DialogueOverlay';
import { EntityInspectCard } from '@/components/game-ui/EntityInspectCard';
import { processEvents } from '@/lib/game-logic/process-events';
import { apiFetch } from '@/lib/api';
import { loadDungeonMap, loadDungeonFromApi } from '@/lib/game-logic/dungeon-loader';
import { GAME_TEMPLATES, type Quest } from '@/lib/game-logic/templates';
import {
  createStretchState,
  stepStretch,
  type StretchState,
  type StretchActivity,
} from '@omen-quest/stretch-engine';
import {
  DEFAULT_RENDER_CONFIG,
  applyInput,
  coordKey,
  type GridMapState,
  type RenderConfig,
  type MoveDirection,
  type Entity,
} from '@omen-quest/grid-map';

// ============================================================================
// TYPES
// ============================================================================

interface GameData {
  id: string;
  name: string;
  region: string;
  adventureTheme: string;
  language: string;
  dungeonId?: string | null;
}

interface CharacterData {
  id: string;
  name: string;
  vitals: number;
  vitalsMax: number;
  resolve: number;
  resolveMax: number;
  stats: { brawn: number; grace: number; wits: number; spirit: number };
  equipment: string[];
  kitType: string;
  race: string;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'playing' };

/**
 * Planning step within a stretch:
 *  'choose'    → pick an activity (no movement allowed)
 *  'move'      → move within the activity's budget, then Lock In
 *  'narrative'  → AI GM narrates results, player interacts
 */
type PlanningStep = 'choose' | 'move' | 'narrative';

// ============================================================================
// CONSTANTS
// ============================================================================

const BASE_MOVEMENT_BUDGET = 6;

const PLAY_RENDER_CONFIG: RenderConfig = {
  ...DEFAULT_RENDER_CONFIG,
  tileSize: 24,
  fogEnabled: true,
  showGrid: true,
  showMinimap: true,
  showTriggers: false,
  showDebugVis: false,
};

/** Movement tiles granted per activity type. */
function getActivityMovement(type: StretchActivity['type']): number {
  switch (type) {
    case 'move':          return BASE_MOVEMENT_BUDGET;       // 6
    case 'explore':       return Math.floor(BASE_MOVEMENT_BUDGET * 0.5); // 3
    case 'move_quietly':  return Math.floor(BASE_MOVEMENT_BUDGET * 0.5); // 3
    case 'interact':      return 1; // just enough to reach adjacent
    default:              return 0; // search, keep_watch, help, loot, cast_spell, take_your_time, wait
  }
}

// ============================================================================
// HELPERS
// ============================================================================

/** Sync the stretch engine's player positions onto the GridMap entities. */
function syncPlayerEntities(state: StretchState): StretchState {
  const updatedEntities = state.map.entities.map(entity => {
    const player = state.players.find(p => p.id === entity.id);
    if (player) {
      return { ...entity, x: player.x, y: player.y };
    }
    return entity;
  });
  return { ...state, map: { ...state.map, entities: updatedEntities } };
}

/** Create a GridMapState from the current StretchState for rendering. */
function createMapStateFromStretch(stretch: StretchState): GridMapState {
  const player = stretch.players[0];
  return {
    map: stretch.map,
    playerId: player.id,
    fogGrid: player.fogGrid,
    lightGrid: player.lightGrid,
    openDoors: stretch.openDoors,
    lockedDoors: new Set(),
    firedTriggers: stretch.firedTriggers,
    triggerFlags: stretch.triggerFlags,
    pendingEvents: [],
    moveCount: 0,
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function Play() {
  const { id: gameId } = useParams<{ id: string }>();

  // Page state
  const [pageState, setPageState] = useState<PageState>({ phase: 'loading' });
  const [game, setGame] = useState<GameData | null>(null);
  const [character, setCharacter] = useState<CharacterData | null>(null);

  // Stretch engine state
  const [stretchState, setStretchState] = useState<StretchState | null>(null);
  const [mapState, setMapState] = useState<GridMapState | null>(null);
  const [renderConfig, setRenderConfig] = useState<RenderConfig>({ ...PLAY_RENDER_CONFIG });

  // Planning flow
  const [planningStep, setPlanningStep] = useState<PlanningStep>('choose');
  const [selectedActivity, setSelectedActivity] = useState<StretchActivity | null>(null);
  const [moveCount, setMoveCount] = useState(0);
  const [narrativeKey, setNarrativeKey] = useState(0);
  const [dungeonName, setDungeonName] = useState('Unknown Dungeon');

  // Quest
  const [quest, setQuest] = useState<Quest | undefined>(undefined);

  // Dialogue overlay and entity inspect
  const [dialogue, setDialogue] = useState<DialogueState | null>(null);
  const [inspectedEntity, setInspectedEntity] = useState<Entity | null>(null);

  // Refs to avoid stale closures
  const stretchRef = useRef(stretchState);
  stretchRef.current = stretchState;
  const dialogueRef = useRef(dialogue);
  dialogueRef.current = dialogue;
  const moveCountRef = useRef(moveCount);
  moveCountRef.current = moveCount;
  const planningStepRef = useRef(planningStep);
  planningStepRef.current = planningStep;
  const selectedActivityRef = useRef(selectedActivity);
  selectedActivityRef.current = selectedActivity;

  // Derived: movement budget for the currently selected activity
  const activityMovementBudget = selectedActivity
    ? getActivityMovement(selectedActivity.type)
    : 0;

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (!gameId) {
      setPageState({ phase: 'error', message: 'No game ID provided' });
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        const [gameRes, charRes] = await Promise.all([
          apiFetch(`/api/games/${gameId}`),
          apiFetch(`/api/games/${gameId}/character`),
        ]);

        const { game: gameData } = await gameRes.json();
        const { character: charData } = await charRes.json();

        if (cancelled) return;

        if (!charData) {
          setPageState({ phase: 'error', message: 'No character found for this game' });
          return;
        }

        let dungeonMap;
        let templateQuest: import('@/lib/game-logic/templates').Quest | undefined;

        if (gameData.dungeonId) {
          // Game uses a designed dungeon from the API
          dungeonMap = await loadDungeonFromApi(gameData.dungeonId);
        } else {
          // Legacy: use a fixture dungeon map from templates
          const template = GAME_TEMPLATES.find(
            t => t.settings.region === gameData.region &&
                 t.settings.adventureTheme === gameData.adventureTheme
          );

          if (!template?.dungeonMapId) {
            setPageState({ phase: 'error', message: 'No dungeon map configured for this game template' });
            return;
          }

          dungeonMap = loadDungeonMap(template.dungeonMapId);
          templateQuest = template.quest;
        }
        const spawnEntity = dungeonMap.entities.find(e => e.type === 'player');
        const spawnX = spawnEntity?.x ?? 1;
        const spawnY = spawnEntity?.y ?? 1;

        dungeonMap.entities = dungeonMap.entities.map(e =>
          e.type === 'player'
            ? { ...e, id: charData.id, name: charData.name }
            : e
        );

        const hasLantern = charData.equipment.includes('Lantern');
        const lightType = hasLantern ? 'lantern' : 'torch';
        const lightRadius = hasLantern ? 8 : 6;

        const initialStretch = createStretchState({
          map: dungeonMap,
          players: [{
            id: charData.id,
            name: charData.name,
            x: spawnX,
            y: spawnY,
            movementBudget: BASE_MOVEMENT_BUDGET,
            visionRadius: 0,
          }],
          crawlStrategy: {
            lightSources: [{
              id: 'starter-light',
              carrierId: charData.id,
              type: lightType,
              remainingStretches: 12,
              radius: lightRadius,
            }],
            stealthMode: false,
          },
        });

        if (cancelled) return;

        const synced = syncPlayerEntities(initialStretch);
        setGame(gameData);
        setCharacter(charData);
        setQuest(templateQuest);
        setStretchState(synced);
        setMapState(createMapStateFromStretch(synced));
        setDungeonName(dungeonMap.name || 'Unknown Dungeon');
        setPageState({ phase: 'playing' });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load game';
        setPageState({ phase: 'error', message });
      }
    }

    init();
    return () => { cancelled = true; };
  }, [gameId]);

  // ============================================================================
  // ACTIVITY SELECTION (step 1: choose → move)
  // ============================================================================

  /** Player picks an activity → transition to movement step. */
  const handleConfirmActivity = useCallback(() => {
    if (!selectedActivity) return;
    setMoveCount(0);
    setPlanningStep('move');
  }, [selectedActivity]);

  /** Go back to activity selection (undo choice). */
  const handleBackToChoose = useCallback(() => {
    setPlanningStep('choose');
    setSelectedActivity(null);
    setMoveCount(0);
  }, []);

  // ============================================================================
  // MOVEMENT (step 2: move within activity budget)
  // ============================================================================

  /** Apply input, process any pending events into dialogue/custom events. */
  const applyAndProcess = useCallback((prev: GridMapState, input: Parameters<typeof applyInput>[1]): GridMapState => {
    const next = applyInput(prev, input);
    if (next.pendingEvents.length > 0) {
      const { dialogue: dialogueState, customEvents } = processEvents(next.pendingEvents);

      // Handle custom quest events — show their text as dialogue
      for (const ce of customEvents) {
        if ((ce.eventName === 'quest_complete' || ce.eventName === 'ward_broken') && ce.data?.text) {
          setDialogue({
            text: ce.data.text as string,
            speaker: 'Narrator',
            variant: ce.eventName === 'quest_complete' ? 'quest' : 'dialogue',
          });
          setInspectedEntity(null);
          return next;
        }
      }

      if (dialogueState) {
        setDialogue(dialogueState);
        setInspectedEntity(null);
      }
    }
    return next;
  }, []);

  const handleMove = useCallback((direction: MoveDirection) => {
    if (!stretchState || stretchState.phase !== 'planning') return;
    if (dialogueRef.current) return;

    // Only allow movement during the 'move' step
    if (planningStepRef.current !== 'move') return;

    // Enforce activity-based movement budget
    const budget = getActivityMovement(selectedActivityRef.current?.type ?? 'wait');
    if (moveCountRef.current >= budget) return;

    setInspectedEntity(null);
    setMapState(prev => {
      if (!prev) return prev;
      return applyAndProcess(prev, { type: 'move', direction });
    });
    setMoveCount(c => c + 1);
  }, [stretchState, applyAndProcess]);

  const handleCellClick = useCallback((x: number, y: number) => {
    if (!mapState || !stretchState || stretchState.phase !== 'planning') return;
    if (dialogue) return;

    const player = mapState.map.entities.find(e => e.id === mapState.playerId);
    if (!player) return;

    const dx = x - player.x;
    const dy = y - player.y;
    const isAdjacent = Math.abs(dx) + Math.abs(dy) === 1;

    // Open adjacent doors on click (always allowed, doesn't cost movement)
    const cell = mapState.map.cells[y]?.[x];
    if (isAdjacent && cell?.terrain === 'door' && !mapState.openDoors.has(coordKey(x, y))) {
      setInspectedEntity(null);
      setMapState(prev => prev ? applyAndProcess(prev, { type: 'toggle_door', x, y }) : prev);
      return;
    }

    // Entity inspection — always allowed
    const entity = mapState.map.entities.find(e => e.x === x && e.y === y);
    if (entity) {
      const isVisible = entity.id === mapState.playerId || mapState.fogGrid[y]?.[x] === 'visible';
      if (isVisible) {
        setInspectedEntity(entity);
        return;
      }
    }

    // Move on adjacent floor click (only during 'move' step)
    if (planningStep !== 'move') return;

    let dir: MoveDirection | null = null;
    if (dx === 0 && dy === -1) dir = 'up';
    else if (dx === 0 && dy === 1) dir = 'down';
    else if (dx === -1 && dy === 0) dir = 'left';
    else if (dx === 1 && dy === 0) dir = 'right';

    if (dir) {
      handleMove(dir);
    }
  }, [mapState, stretchState, dialogue, planningStep, applyAndProcess, handleMove]);

  // ============================================================================
  // COMMIT (step 2 → step 3: resolve stretch, enter narrative)
  // ============================================================================

  const handleCommit = useCallback(() => {
    if (!stretchState || !mapState || !character || !selectedActivity) return;
    if (stretchState.phase !== 'planning') return;

    // Sync player position from mapState back to stretchState
    const playerEntity = mapState.map.entities.find(e => e.id === character.id);
    let preCommitState = stretchState;
    if (playerEntity) {
      const updatedPlayers = stretchState.players.map(p =>
        p.id === character.id ? { ...p, x: playerEntity.x, y: playerEntity.y } : p
      );
      preCommitState = { ...stretchState, players: updatedPlayers };
    }

    // Commit → resolve → events (all instant, client-side)
    let state = stepStretch(preCommitState, {
      type: 'commit',
      playerId: character.id,
      activity: selectedActivity,
    });

    if (state.phase === 'resolving') {
      state = stepStretch(state, { type: 'step' });
    }

    if (state.phase === 'events') {
      state = stepStretch(state, { type: 'step' });
    }

    const synced = syncPlayerEntities(state);
    setStretchState(synced);
    setMapState(createMapStateFromStretch(synced));
    setSelectedActivity(null);
    setMoveCount(0);

    // Enter narrative mode
    setPlanningStep('narrative');
    setNarrativeKey(k => k + 1);
  }, [stretchState, mapState, character, selectedActivity]);

  // ============================================================================
  // NARRATIVE → NEXT STRETCH (step 3 → step 1)
  // ============================================================================

  const handleNextStretch = useCallback(() => {
    setPlanningStep('choose');
    setSelectedActivity(null);
    setMoveCount(0);
  }, []);

  // ============================================================================
  // KEYBOARD HANDLER
  // ============================================================================

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLSelectElement ||
        e.target instanceof HTMLTextAreaElement
      ) return;

      const current = stretchRef.current;
      if (!current || current.phase !== 'planning') return;

      // ESC: dismiss inspect card, or go back from move → choose
      if (e.key === 'Escape') {
        if (inspectedEntity) {
          setInspectedEntity(null);
          return;
        }
        if (planningStepRef.current === 'move') {
          handleBackToChoose();
          return;
        }
      }

      if (dialogueRef.current) return;

      // WASD / Arrow movement (only during 'move' step)
      const dirMap: Record<string, MoveDirection> = {
        w: 'up', W: 'up', ArrowUp: 'up',
        s: 'down', S: 'down', ArrowDown: 'down',
        a: 'left', A: 'left', ArrowLeft: 'left',
        d: 'right', D: 'right', ArrowRight: 'right',
      };

      const dir = dirMap[e.key];
      if (dir) {
        e.preventDefault();
        handleMove(dir);
        return;
      }

      // F: toggle fog
      if (e.key === 'f' || e.key === 'F') {
        setRenderConfig(prev => ({ ...prev, fogEnabled: !prev.fogEnabled }));
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleMove, handleBackToChoose, inspectedEntity]);

  // ============================================================================
  // RENDER — LOADING
  // ============================================================================

  if (pageState.phase === 'loading') {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-neutral-400 animate-spin" />
          <p className="text-sm text-neutral-500">Loading game...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER — ERROR
  // ============================================================================

  if (pageState.phase === 'error') {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 max-w-md text-center px-4">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <p className="text-sm text-neutral-300">{pageState.message}</p>
          <Link
            to="/games"
            className="text-sm text-neutral-500 hover:text-white transition-colors flex items-center gap-1.5 mt-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Games
          </Link>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER — PLAYING
  // ============================================================================

  const stepLabels: Record<PlanningStep, string> = {
    choose: 'Choose Activity',
    move: 'Move & Commit',
    narrative: 'Narrative',
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* ── Header ── */}
      <header className="relative z-20 flex items-center gap-3 border-b border-neutral-800 bg-neutral-950 px-4 py-2.5">
        <Link
          to="/games"
          className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>

        <div className="w-px h-5 bg-white/10" />

        <h1 className="text-sm font-medium text-neutral-200 truncate">
          {game?.name ?? 'Untitled Game'}
        </h1>

        <div className="flex-1" />

        {stretchState && (
          <span className="text-xs text-neutral-500">
            Stretch #{stretchState.stretchNumber}
            <span className={
              planningStep === 'narrative' ? 'ml-2 text-amber-500'
                : planningStep === 'move' ? 'ml-2 text-blue-400'
                : 'ml-2 text-neutral-400'
            }>
              — {stepLabels[planningStep]}
            </span>
          </span>
        )}
      </header>

      {/* ── Main area ── */}
      <div className="flex flex-1 min-h-0">
        {/* Map canvas */}
        <div className="relative flex-1 min-w-0 min-h-0">
          {mapState ? (
            <GridMapCanvas
              state={mapState}
              config={renderConfig}
              onCellClick={handleCellClick}
              onMoveRequest={handleMove}
              className="w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-neutral-600">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {/* Entity inspect card */}
          {inspectedEntity && !dialogue && (
            <EntityInspectCard
              entity={inspectedEntity}
              onDismiss={() => setInspectedEntity(null)}
            />
          )}

          {/* Dialogue overlay */}
          {dialogue && (
            <DialogueOverlay
              text={dialogue.text}
              speaker={dialogue.speaker}
              variant={dialogue.variant}
              onDismiss={() => setDialogue(null)}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="w-[380px] shrink-0 hidden md:block">
          {character && stretchState && game && (
            <PlaySidebar
              character={character}
              stretchNumber={stretchState.stretchNumber}
              stretchPhase={stretchState.phase}
              planningStep={planningStep}
              moveCount={moveCount}
              movementBudget={activityMovementBudget}
              selectedActivity={selectedActivity}
              onSelectActivity={setSelectedActivity}
              onConfirmActivity={handleConfirmActivity}
              onBackToChoose={handleBackToChoose}
              onCommit={handleCommit}
              lastResolution={stretchState.lastResolution}
              quest={quest}
              triggerFlags={stretchState.triggerFlags}
              disabled={stretchState.phase !== 'planning'}
            >
              {planningStep === 'narrative' && (
                <NarrativePanel
                  key={narrativeKey}
                  gameId={game.id}
                  character={character}
                  game={{
                    region: game.region,
                    adventureTheme: game.adventureTheme,
                    language: game.language,
                  }}
                  stretchResults={stretchState.lastResolution}
                  dungeonName={dungeonName}
                  playerPosition={(() => {
                    const player = mapState?.map.entities.find(e => e.id === character.id);
                    return player ? { x: player.x, y: player.y } : undefined;
                  })()}
                  onNextStretch={handleNextStretch}
                />
              )}
            </PlaySidebar>
          )}
        </div>
      </div>
    </div>
  );
}
